import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const themePresets = {
  cyber_blue: {
    name: 'Cyber Blue',
    primary: '#00F0FF',
    secondary: '#007AFF',
    accent: '#FFFFFF',
    glow: '0 0 20px rgba(0, 240, 255, 0.5)',
    glowHover: '0 0 30px rgba(0, 240, 255, 0.7)',
  },
  neon_purple: {
    name: 'Neon Purple',
    primary: '#D946EF',
    secondary: '#8B5CF6',
    accent: '#F0ABFC',
    glow: '0 0 20px rgba(217, 70, 239, 0.5)',
    glowHover: '0 0 30px rgba(217, 70, 239, 0.7)',
  },
  toxic_green: {
    name: 'Toxic Green',
    primary: '#39FF14',
    secondary: '#10B981',
    accent: '#D1FAE5',
    glow: '0 0 20px rgba(57, 255, 20, 0.5)',
    glowHover: '0 0 30px rgba(57, 255, 20, 0.7)',
  },
  custom_1: {
    name: 'Custom Theme 1',
    primary: '#FF6B35',
    secondary: '#F7C948',
    accent: '#FFECD2',
    glow: '0 0 20px rgba(255, 107, 53, 0.5)',
    glowHover: '0 0 30px rgba(255, 107, 53, 0.7)',
    isCustom: true,
  },
};

const customColorPalettes = {
  sunset_orange: { name: 'Sunset Orange', primary: '#FF6B35', secondary: '#F7C948', accent: '#FFECD2' },
  pure_white: { name: 'Pure White', primary: '#FFFFFF', secondary: '#CBD5E1', accent: '#F1F5F9' },
  silver_steel: { name: 'Silver Steel', primary: '#C0C0C0', secondary: '#8B95A5', accent: '#E2E8F0' },
  ruby_red: { name: 'Ruby Red', primary: '#EF4444', secondary: '#DC2626', accent: '#FCA5A5' },
  crimson_fire: { name: 'Crimson Fire', primary: '#FF2D55', secondary: '#E11D48', accent: '#FDA4AF' },
  golden_yellow: { name: 'Golden Yellow', primary: '#F59E0B', secondary: '#D97706', accent: '#FDE68A' },
  electric_lime: { name: 'Electric Lime', primary: '#84CC16', secondary: '#65A30D', accent: '#D9F99D' },
  ocean_teal: { name: 'Ocean Teal', primary: '#14B8A6', secondary: '#0D9488', accent: '#99F6E4' },
  cyber_pink: { name: 'Cyber Pink', primary: '#EC4899', secondary: '#DB2777', accent: '#F9A8D4' },
  royal_blue: { name: 'Royal Blue', primary: '#3B82F6', secondary: '#2563EB', accent: '#93C5FD' },
  deep_violet: { name: 'Deep Violet', primary: '#8B5CF6', secondary: '#7C3AED', accent: '#C4B5FD' },
  rose_gold: { name: 'Rose Gold', primary: '#F472B6', secondary: '#C9A87C', accent: '#FECDD3' },
  ice_blue: { name: 'Ice Blue', primary: '#67E8F9', secondary: '#22D3EE', accent: '#CFFAFE' },
  neon_mint: { name: 'Neon Mint', primary: '#34D399', secondary: '#10B981', accent: '#A7F3D0' },
  plasma_purple: { name: 'Plasma Purple', primary: '#A855F7', secondary: '#9333EA', accent: '#D8B4FE' },
  copper_rust: { name: 'Copper Rust', primary: '#EA580C', secondary: '#C2410C', accent: '#FDBA74' },
  arctic_silver: { name: 'Arctic Silver', primary: '#94A3B8', secondary: '#64748B', accent: '#CBD5E1' },
  hot_magenta: { name: 'Hot Magenta', primary: '#E879F9', secondary: '#C026D3', accent: '#F0ABFC' },
  chrome_gold: { name: 'Chrome Gold', primary: '#EAB308', secondary: '#CA8A04', accent: '#FEF08A' },
  electric_indigo: { name: 'Electric Indigo', primary: '#6366F1', secondary: '#4F46E5', accent: '#A5B4FC' },
};

const fontOptions = [
  { name: 'Orbitron', value: "'Orbitron', sans-serif" },
  { name: 'Rajdhani', value: "'Rajdhani', sans-serif" },
  { name: 'Space Grotesk', value: "'Space Grotesk', sans-serif" },
  { name: 'Exo 2', value: "'Exo 2', sans-serif" },
  { name: 'Audiowide', value: "'Audiowide', cursive" },
];

const defaultCustomSettings = {
  headerImage: '',
  bodyImage: '',
  heroImage: '',
  overlayOpacity: 70,
  headerOverlayOpacity: 60,
  heroOverlayOpacity: 70,
  headerStyle: 'straight',
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentPreset, setCurrentPreset] = useState('cyber_blue');
  const [currentFont, setCurrentFont] = useState('Orbitron');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [customSettings, setCustomSettings] = useState(defaultCustomSettings);
  const [customColorKey, setCustomColorKey] = useState('sunset_orange');
  const [globalTheme, setGlobalTheme] = useState(null);
  const [globalLoaded, setGlobalLoaded] = useState(false);

  useEffect(() => {
    const loadGlobalTheme = async () => {
      try {
        const res = await axios.get(`${API}/global-theme`);
        const data = res.data;
        if (data && Object.keys(data).length > 0) {
          setGlobalTheme(data);
          if (data.preset && themePresets[data.preset]) {
            setCurrentPreset(data.preset);
          }
          if (data.font) {
            setCurrentFont(data.font);
          }
          if (data.customColor && customColorPalettes[data.customColor]) {
            setCustomColorKey(data.customColor);
          }
          if (data.customSettings) {
            setCustomSettings(prev => ({ ...prev, ...data.customSettings }));
          }
        } else {
          const savedPreset = localStorage.getItem('beatlab_theme_preset');
          const savedFont = localStorage.getItem('beatlab_theme_font');
          const savedMode = localStorage.getItem('beatlab_dark_mode');
          const savedCustom = localStorage.getItem('beatlab_custom_settings');
          const savedColorKey = localStorage.getItem('beatlab_custom_color');

          if (savedPreset && themePresets[savedPreset]) setCurrentPreset(savedPreset);
          if (savedFont) setCurrentFont(savedFont);
          if (savedMode !== null) setIsDarkMode(savedMode === 'true');
          if (savedColorKey && customColorPalettes[savedColorKey]) setCustomColorKey(savedColorKey);
          if (savedCustom) {
            try { setCustomSettings(s => ({ ...s, ...JSON.parse(savedCustom) })); } catch {}
          }
        }
      } catch {
        const savedPreset = localStorage.getItem('beatlab_theme_preset');
        const savedFont = localStorage.getItem('beatlab_theme_font');
        const savedMode = localStorage.getItem('beatlab_dark_mode');
        const savedCustom = localStorage.getItem('beatlab_custom_settings');
        const savedColorKey = localStorage.getItem('beatlab_custom_color');

        if (savedPreset && themePresets[savedPreset]) setCurrentPreset(savedPreset);
        if (savedFont) setCurrentFont(savedFont);
        if (savedMode !== null) setIsDarkMode(savedMode === 'true');
        if (savedColorKey && customColorPalettes[savedColorKey]) setCustomColorKey(savedColorKey);
        if (savedCustom) {
          try { setCustomSettings(s => ({ ...s, ...JSON.parse(savedCustom) })); } catch {}
        }
      }
      setGlobalLoaded(true);
    };
    loadGlobalTheme();
  }, []);

  const isCustomTheme = currentPreset === 'custom_1';

  const getActiveTheme = useCallback(() => {
    if (isCustomTheme) {
      const palette = customColorPalettes[customColorKey] || customColorPalettes.sunset_orange;
      return {
        ...themePresets.custom_1,
        primary: palette.primary,
        secondary: palette.secondary,
        accent: palette.accent,
        glow: `0 0 20px ${hexToRgba(palette.primary, 0.5)}`,
        glowHover: `0 0 30px ${hexToRgba(palette.primary, 0.7)}`,
      };
    }
    return themePresets[currentPreset];
  }, [currentPreset, isCustomTheme, customColorKey]);

  useEffect(() => {
    const theme = getActiveTheme();
    const root = document.documentElement;
    
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-secondary', theme.secondary);
    root.style.setProperty('--theme-accent', theme.accent);
    root.style.setProperty('--theme-glow', theme.glow);
    root.style.setProperty('--theme-glow-hover', theme.glowHover);
    
    const font = fontOptions.find(f => f.name === currentFont);
    if (font) {
      root.style.setProperty('--theme-font', font.value);
    }

    if (isCustomTheme) {
      root.style.setProperty('--bg-primary', '#0a0a0a');
      root.style.setProperty('--bg-secondary', '#111111');
      root.style.setProperty('--bg-card', `rgba(0, 0, 0, ${(customSettings.overlayOpacity / 100).toFixed(2)})`);
      root.style.setProperty('--text-primary', '#ffffff');
      root.style.setProperty('--text-secondary', '#e2e8f0');
      root.style.setProperty('--text-muted', '#94a3b8');
      root.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.08)');
      document.body.classList.remove('light-mode');
      document.body.classList.add('dark-mode');
      document.body.classList.add('custom-theme');
    } else {
      document.body.classList.remove('custom-theme');

      if (isDarkMode) {
        root.style.setProperty('--bg-primary', '#050505');
        root.style.setProperty('--bg-secondary', '#0a0a0a');
        root.style.setProperty('--bg-card', 'rgba(0, 0, 0, 0.4)');
        root.style.setProperty('--text-primary', '#ffffff');
        root.style.setProperty('--text-secondary', '#d1d5db');
        root.style.setProperty('--text-muted', '#9ca3af');
        root.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.1)');
        document.body.classList.remove('light-mode');
        document.body.classList.add('dark-mode');
      } else {
        root.style.setProperty('--bg-primary', '#f8fafc');
        root.style.setProperty('--bg-secondary', '#f1f5f9');
        root.style.setProperty('--bg-card', 'rgba(255, 255, 255, 0.98)');
        root.style.setProperty('--text-primary', '#0f172a');
        root.style.setProperty('--text-secondary', '#1e293b');
        root.style.setProperty('--text-muted', '#475569');
        root.style.setProperty('--border-color', 'rgba(0, 0, 0, 0.15)');
        document.body.classList.remove('dark-mode');
        document.body.classList.add('light-mode');
      }
    }
  }, [currentPreset, currentFont, isDarkMode, isCustomTheme, customSettings, customColorKey, getActiveTheme]);

  const setPreset = (preset) => {
    if (themePresets[preset]) {
      setCurrentPreset(preset);
      localStorage.setItem('beatlab_theme_preset', preset);
      if (preset === 'custom_1' && !isDarkMode) {
        setIsDarkMode(true);
        localStorage.setItem('beatlab_dark_mode', 'true');
      }
    }
  };

  const setFont = (font) => {
    setCurrentFont(font);
    localStorage.setItem('beatlab_theme_font', font);
  };

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('beatlab_dark_mode', String(newMode));
  };

  const updateCustomSettings = (updates) => {
    setCustomSettings(prev => {
      const next = { ...prev, ...updates };
      localStorage.setItem('beatlab_custom_settings', JSON.stringify(next));
      return next;
    });
  };

  const setCustomColor = (colorKey) => {
    if (customColorPalettes[colorKey]) {
      setCustomColorKey(colorKey);
      localStorage.setItem('beatlab_custom_color', colorKey);
    }
  };

  const applyGlobalTheme = async (token) => {
    try {
      const payload = {
        preset: currentPreset,
        font: currentFont,
        customColor: customColorKey,
        customSettings: isCustomTheme ? customSettings : null,
      };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/admin/global-theme`, payload, { headers });
      setGlobalTheme(payload);
      return true;
    } catch {
      return false;
    }
  };

  const clearGlobalTheme = async (token) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/admin/global-theme`, { headers });
      setGlobalTheme(null);
      return true;
    } catch {
      return false;
    }
  };

  const theme = getActiveTheme();

  return (
    <ThemeContext.Provider
      value={{
        theme,
        currentPreset,
        currentFont,
        isDarkMode,
        isCustomTheme,
        customSettings,
        customColorKey,
        customColorPalettes,
        globalTheme,
        globalLoaded,
        setPreset,
        setFont,
        setCustomColor,
        toggleDarkMode,
        updateCustomSettings,
        applyGlobalTheme,
        clearGlobalTheme,
        themePresets,
        fontOptions,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
